import Vue from 'vue';
import Uri from 'jsuri';
import axios from 'axios';
import Cookies from 'js-cookie';

import * as constants from '../../constants';
import designArea from '../../utilities/designArea';
import extent from '../../utilities/extent';

// import designArea from '../../utilities/designArea';

// Debounce timer for price updates.
let updatePriceTimer = null;

// Debounce timer for mirroring updates.
let updateMirroringTimer = null;

/**
 * Checks if component is filled.
 */
// eslint-disable-next-line no-unused-vars
function isFilled(component) {
    if (component.custom['widget-type'] === 'personalization') {
        return (component.custom['personalization-text'] || '').length > 0
            || (component.custom['personalization-filled'] === 'true');
    }

    if (component.custom['widget-type'] === 'dynamic-image') {
        return (component.custom['dynamic-image-url'] || '').length > 0;
    }

    return !component.placeholder;
}

/**
 * Checks if placement is being mirrored from another.
 */
function isMirrored(placement) {
    return placement && placement.custom && placement.custom['v-mirror-from'];
}

export default {
    state() {
        return {
            // Widget option as passed from the host site.
            options: {},

            // Default controller parameters.
            defaultControllerOptions: {},

            // Controller that drives the image view.
            viewController: null,

            // Selected size data.
            selectedSize: null,

            // Quantity.
            quantity: 1,

            // Product price.
            productPrice: null,

            // Customizations price.
            customizationsPrice: null,

            // Subtotal price.
            subtotalPrice: null,

            // Subtotal unit price.
            subtotalUnitPrice: null,

            // Standard / default unit price
            defaultUnitPrice: null,

            // Standard / default price for the current quantity
            defaultSubtotalPrice: null,

            // Personalization method.
            personalizationMethod: null,

            // Selected design sides.
            designSide: 'front',

            // Currently selected view.
            currentView: null,

            // Placements being edited.
            editPlacements: null,

            // Loaded fonts.
            loadedFonts: {},

            // Selected bottle.
            selectedBottle: null,

            // If monogram is being added or edited.
            addEditMonogram: 'edit',

            // If tex is being added or edited.
            addEditText: 'edit',

            // Secondary bottles.
            secondaryBottles: [],

            // Temporarily hidden bottles.
            stashedBottles: [],

            // Saved placement settings.
            savedPlacements: null,

            // Number of personalizations.
            personalizations: 0,

            // Number of free personalizations.
            freePersonalizations: 0,
        };
    },

    getters: {
        /**
         * Get product specific placements.
         */
        productLayers(state) {
            if (state.viewController == null) {
                return [];
            }

            const productGroup = state.viewController.blueprint.groups.find((group) => group.code === constants.GROUP_PRODUCT);

            if (productGroup == null) {
                return [];
            }

            const foundPlacements = [];

            productGroup.placements.forEach((p) => {
                if (p.custom?.['v-hidden'] === 'true') {
                    return;
                }

                const selected = state.viewController.selectedAtPlacement(p.code);

                foundPlacements.push({
                    main: p.custom?.['v-main'] === 'true',
                    title: p.custom?.['v-title'],
                    placement: state.viewController.findPlacement(p.code),
                    component: selected,
                    selectedName: state.viewController.componentNames(selected).join(''),
                });
            });

            return foundPlacements;
        },

        /**
         * Gets currently selected color name.
         */
        selectedColor(state) {
            if (state.viewController == null) {
                return '';
            }

            const product = state.viewController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

            if (product != null) {
                return product.description.find((d) => d.definition === constants.DEFINITION_PRODUCT).description;
            }

            return '';
        },

        /**
         * Gets currently selected color hex color.
         */
        selectedColorHex(state) {
            if (state.viewController == null) {
                return '';
            }

            const product = state.viewController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

            if (product != null) {
                const { hex } = product.custom;

                if (hex) {
                    return hex.startsWith('#') ? hex : `#${hex}`;
                }
            }

            return '';
        },

        /**
         * Gets currently selected color menu caption.
         */
        colorStepTitle(state) {
            if (state.viewController == null) {
                return '';
            }

            return state.viewController.blueprint.custom['v-color-step-title'] || 'Color';
        },

        /**
         * Could the design be built?
         */
        canBuild(state, getters) {
            return getters.canBuildReason == null;
        },

        /**
         * Could the design be built?
         */
        canBuildReason(state) {
            if (state.viewController) {
                if (!state.viewController.canBuild({ ignoreOptional: true })) {
                    return 'components';
                }
            } else {
                return 'controller';
            }

            return null;
        },

        allPlacements(state) {
            if (state.viewController) {
                return state.viewController.state.filter((p) => {
                    if (p.placement.code.startsWith(constants.PLACEMENT_IMAGE) && !p.placement.code.startsWith('ImageGallery')) {
                        return true;
                    }

                    if (p.placement.code.startsWith(constants.PLACEMENT_TEXT)) {
                        return true;
                    }

                    if (p.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)) {
                        return true;
                    }

                    return false;
                });
            }

            return [];
        },

        /**
         * Get a list of placements with filled text.
         */
        filledPlacements(state, getters) {
            const placements = getters.allPlacements.filter((p) => isFilled(p.component) && !isMirrored(p.placement));

            placements.sort((a, b) => {
                const aIndex = +a.placement.custom['explicit-order'];
                const bIndex = +b.placement.custom['explicit-order'];

                return aIndex - bIndex;
            });

            return placements;
        },

        /**
         * Get a list of placements with images.
         */
        imagePlacements(state) {
            if (state.viewController) {
                return state.viewController.state.filter((p) => {
                    if (p.placement.code.startsWith(constants.PLACEMENT_IMAGE) && p.placement.code !== constants.PLACEMENT_IMAGE_GALLERY) {
                        return true;
                    }

                    return false;
                });
            }

            return [];
        },

        /**
         * Get a list of placements with filled images.
         */
        filledImagePlacements(state, getters) {
            return getters.imagePlacements.filter((p) => isFilled(p.component) && !isMirrored(p.placement));
        },

        /**
         * Get a list of empty image placements.
         */
        emptyImagePlacements(state, getters) {
            return getters.imagePlacements.filter((p) => !isFilled(p.component));
        },

        /**
         * Get a list of placements with text.
         */
        textPlacements(state) {
            if (state.viewController) {
                return state.viewController.state.filter((p) => p.placement.code.startsWith(constants.PLACEMENT_TEXT));
            }

            return [];
        },

        /**
         * Get a list of placements with filled text.
         */
        filledTextPlacements(state, getters) {
            return getters.textPlacements.filter((p) => isFilled(p.component) && !isMirrored(p.placement));
        },

        /**
         * Get a list of empty text placements.
         */
        emptyTextPlacements(state, getters) {
            return getters.textPlacements.filter((p) => !isFilled(p.component));
        },
        /**
         * Get a list of placements with monogram.
         */
        monogramPlacements(state) {
            if (state.viewController) {
                return state.viewController.state.filter((p) => {
                    if (p.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)) {
                        return true;
                    }

                    return false;
                });
            }

            return [];
        },

        /**
         * Get a list of placements with filled text.
         */
        filledMonogramPlacements(state, getters) {
            return getters.monogramPlacements.filter((p) => isFilled(p.component) && !isMirrored(p.placement));
        },

        /**
         * Get a list of empty text placements.
         */
        emptyMonogramPlacements(state, getters) {
            return getters.monogramPlacements.filter((p) => !isFilled(p.component));
        },

        /**
         * Gets a list of available personalization colors.
         * @return {[type]} [description]
         */
        availablePersonalizationColors(state) {
            let exclude = null;

            const selected = state.viewController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);
            if (selected && selected.custom && selected.custom['v-exclusions']) {
                exclude = selected.custom['v-exclusions'];

                exclude = exclude.split(',').map((x) => x.trim());
            }

            const colors = [];

            const colorsPlacement = state.viewController.findPlacement(constants.PLACEMENT_COLORS);

            colorsPlacement.components.forEach((c) => {
                if (exclude && c.custom && c.custom['v-exclusions']) {
                    const e = c.custom['v-exclusions'].split(',').map((x) => x.trim());

                    if (e.some((x) => exclude.includes(x))) {
                        return;
                    }
                }

                const color = c.description.find((d) => d.definition === constants.DEFINITION_COLOR);

                if (color) {
                    if (!colors.some((f) => f.code === color.code)) {
                        const copy = { ...color };

                        if (copy.custom == null) {
                            copy.custom = {};
                        }

                        copy.custom = { ...copy.custom, ...c.custom };

                        colors.push(copy);
                    }
                }
            });

            return colors;
        },

        /**
         * Available quantity for the current colorway.
         */
        availableQuantity(state, getters, rootState) {
            if (state.viewController == null) {
                return 0;
            }

            const selected = state.viewController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

            if (selected == null) {
                return 0;
            }

            let ats = 0;

            const product = (rootState.ecommerce.products || []).find((p) => p.productId === selected.placementIndependentCode);
            if (product && product.online) {
                ats = product.ats;
            }

            return ats;
        },

        /**
         * Checks if the controller supports mirroring configuration.
         */
        supportsMirroring(state) {
            // V3: Mirroring disabled.
            return false;

            // eslint-disable-next-line no-unreachable
            // const controller = state.viewController;

            // if (controller) {
            //     if (controller.blueprint.custom['v-allow-mirroring'] !== 'true') {
            //         return false;
            //     }

            //     const designOn = (controller.blueprint.custom['v-design-on'] || '').split(',').map((x) => x.trim());

            //     if (designOn.includes('front') && designOn.includes('back')) {
            //         const frontWidth = +controller.blueprint.custom['v-area-front-width'];
            //         const frontHeight = +controller.blueprint.custom['v-area-front-height'];

            //         const backWidth = +controller.blueprint.custom['v-area-back-width'];
            //         const backHeight = +controller.blueprint.custom['v-area-back-height'];

            //         if (frontWidth === backWidth && frontHeight === backHeight) {
            //             return true;
            //         }
            //     }
            // }

            // return false;
        },
        /**
         * Generate a list of available components.
         */
        availableColorsV3(state, getters, rootState) {
            if (state.viewController == null) {
                return [];
            }

            const productPlacement = state.viewController.findPlacement(constants.PLACEMENT_PRODUCT);

            const selected = state.viewController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

            const productData = rootState.ecommerce.products;

            const colors = productPlacement.components.map((c) => {
                let ats = 0;

                const product = productData.find((p) => p.productId === c.placementIndependentCode);
                if (product && product.online) {
                    ats = product.ats;
                }

                const color = {
                    option: c,
                    selected: selected.code === c.code,
                    ats,
                };

                const definition = c.description.find((d) => d.definition === constants.DEFINITION_PRODUCT);

                if (definition) {
                    color.name = definition.description;
                }

                return color;
            });

            return colors.filter((color) => {
                if (color.ats == null || color.ats === 0) {
                    return false;
                }

                return true;
            });
        },

        availablePrintingColorsV3(state, getters) {
            return getters.availableColorsV3.filter((color) => {
                if (color.option.custom['v-supports-printing'] === 'true') {
                    return true;
                }

                return false;
            });
        },

        availableEtchingColorsV3(state, getters) {
            return getters.availableColorsV3.filter((color) => {
                if (color.option.custom['v-supports-etching'] === 'true') {
                    return true;
                }

                return false;
            });
        },

        availableSidesV3(state) {
            if (state.viewController === null) {
                return [];
            }

            if (state.personalizationMethod === 'printing') {
                const designOn = state.viewController.blueprint.custom['v-design-on'];

                if (designOn) {
                    return designOn.split(',').map((x) => x.trim());
                }
            }

            if (state.personalizationMethod === 'etching') {
                const designOn = state.viewController.blueprint.custom['v-design-on'];

                if (designOn) {
                    const sides = designOn.split(',').map((x) => x.trim());

                    if (state.viewController.blueprint.custom['v-etch-either-side'] === 'true') {
                        const preferred = state.viewController.blueprint.custom['v-preferred-side-v3'];

                        if (preferred && sides.includes(preferred)) {
                            return [preferred, preferred];
                        }
                    }

                    const all = [];

                    if (sides.includes('front')) {
                        all.push('front', 'front');
                    }

                    if (sides.includes('back')) {
                        all.push('back', 'back');
                    }

                    if (sides.includes('backtop')) {
                        all.push('backtop', 'backtop');
                    }

                    return all;
                }
            }

            return [];
        },

        nextStepV3(state, getters, rootState) {
            if (rootState.selectedStepV3 === 'start') {
                const totalColors = getters.availablePrintingColorsV3.length + getters.availableEtchingColorsV3.length;

                if (totalColors > 1) {
                    return 'itemColors';
                }

                return 'design-1';
            }

            if (rootState.selectedStepV3 === 'itemColors') {
                return 'design-1';
            }

            if (state.personalizationMethod === 'printing') {
                if (rootState.selectedStepV3 === 'design-1') {
                    const available = getters.availableSidesV3;

                    if (available.length > 1) {
                        return 'design-2';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-2') {
                    return 'review';
                }
            }

            if (state.personalizationMethod === 'etching') {
                const available = getters.availableSidesV3;

                if (rootState.selectedStepV3 === 'design-1') {
                    if (available.length > 1) {
                        return 'design-1-1';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-1-1') {
                    if (available.length > 2) {
                        return 'design-2';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-2') {
                    if (available.length > 3) {
                        return 'design-2-1';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-2-1') {
                    if (available.length > 4) {
                        return 'design-3';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-3') {
                    if (available.length > 5) {
                        return 'design-3-1';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-3-1') {
                    return 'review';
                }

                return 'review';
            }

            return null;
        },

        nextStepWithSkipV3(state, getters, rootState) {
            if (rootState.selectedStepV3 === 'start') {
                const totalColors = getters.availablePrintingColorsV3.length + getters.availableEtchingColorsV3.length;

                if (totalColors > 1) {
                    return 'itemColors';
                }

                return 'design-1';
            }

            if (rootState.selectedStepV3 === 'itemColors') {
                return 'design-1';
            }

            if (state.personalizationMethod === 'printing') {
                if (rootState.selectedStepV3 === 'design-1') {
                    const available = getters.availableSidesV3;

                    if (available.length > 1) {
                        return 'design-2';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-2') {
                    return 'review';
                }
            }

            if (state.personalizationMethod === 'etching') {
                const available = getters.availableSidesV3;

                if (rootState.selectedStepV3 === 'design-1') {
                    if (available.length > 2) {
                        return 'design-2';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-1-1') {
                    if (available.length > 2) {
                        return 'design-2';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-2') {
                    if (available.length > 4) {
                        return 'design-3';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-2-1') {
                    if (available.length > 4) {
                        return 'design-3';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-3') {
                    if (available.length > 6) {
                        return 'design-3-1';
                    }

                    return 'review';
                }

                if (rootState.selectedStepV3 === 'design-3-1') {
                    return 'review';
                }

                return 'review';
            }

            return null;
        },

        allStepsV3(state, getters, rootState) {
            const steps = [];

            const totalColors = getters.availablePrintingColorsV3.length + getters.availableEtchingColorsV3.length;

            if (totalColors > 1) {
                steps.push({
                    step: 'itemColors',
                    seen: rootState.seenStepsV3.includes('itemColors'),
                });
            }

            const available = getters.availableSidesV3;

            if (available.length > 0) {
                const designOn = available[0];

                const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                steps.push({
                    step: 'design-1',
                    seen: rootState.seenStepsV3.includes('design-1') && allOnThisSide.length > 0,
                });

                steps.push({
                    step: 'design-1-1',
                    seen: rootState.seenStepsV3.includes('design-1-1') && allOnThisSide.length > 1,
                });
            }

            if (available.length > 2) {
                const designOn = available[2];

                const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                steps.push({
                    step: 'design-2',
                    seen: rootState.seenStepsV3.includes('design-2') && allOnThisSide.length > 0,
                });

                steps.push({
                    step: 'design-2-1',
                    seen: rootState.seenStepsV3.includes('design-2-1') && allOnThisSide.length > 1,
                });
            }

            if (available.length > 4) {
                const designOn = available[4];

                const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                steps.push({
                    step: 'design-3',
                    seen: rootState.seenStepsV3.includes('design-3') && allOnThisSide.length > 0,
                });

                steps.push({
                    step: 'design-3-1',
                    seen: rootState.seenStepsV3.includes('design-3-1') && allOnThisSide.length > 1,
                });
            }

            steps.push({
                step: 'review',
                seen: rootState.seenStepsV3.includes('review'),
            });

            return steps;
        },

        prevStepV3(state, getters, rootState) {
            if (rootState.selectedStepV3 === 'start') {
                return null;
            }

            if (rootState.selectedStepV3 === 'itemColors') {
                return 'start';
            }

            if (rootState.selectedStepV3 === 'design-1') {
                const totalColors = getters.availablePrintingColorsV3.length + getters.availableEtchingColorsV3.length;

                if (totalColors > 1) {
                    return 'itemColors';
                }

                return 'start';
            }

            const available = [...getters.availableSidesV3];

            if (rootState.selectedStepV3 === 'design-3-1') {
                return 'design-3';
            }

            if (rootState.selectedStepV3 === 'design-3') {
                const designOn = available[3];

                const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                if (allOnThisSide.length > 1) {
                    return 'design-2-1';
                }

                return 'design-2';
            }

            if (rootState.selectedStepV3 === 'design-2-1') {
                return 'design-2';
            }

            if (rootState.selectedStepV3 === 'design-2') {
                const designOn = available[1];

                const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                if (allOnThisSide.length > 1) {
                    return 'design-1-1';
                }

                return 'design-1';
            }

            if (rootState.selectedStepV3 === 'design-1-1') {
                return 'design-1';
            }

            if (rootState.selectedStepV3 === 'review') {
                if (state.personalizationMethod === 'printing') {
                    if (available.length > 1) {
                        return 'design-2';
                    }
                }

                if (state.personalizationMethod === 'etching') {
                    if (available.length > 5) {
                        const designOn = available[5];

                        const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                        if (allOnThisSide.length > 1) {
                            return 'design-3-1';
                        }

                        return 'design-3';
                    }

                    if (available.length > 4) {
                        return 'design-3';
                    }

                    if (available.length > 3) {
                        const designOn = available[3];

                        const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                        if (allOnThisSide.length > 1) {
                            return 'design-2-1';
                        }

                        return 'design-2';
                    }

                    if (available.length > 2) {
                        return 'design-2';
                    }

                    if (available.length > 1) {
                        const designOn = available[1];

                        const allOnThisSide = getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === designOn);

                        if (allOnThisSide.length > 1) {
                            return 'design-1-1';
                        }

                        return 'design-1';
                    }

                    return 'design-1';
                }

                return 'design-1';
            }

            return null;
        },
    },

    mutations: {
        /**
         * Saves options to state.
         */
        SET_CUSTOMIZER_OPTIONS(state, options) {
            state.options = options;

            state.defaultControllerOptions = {
                apiKey: options.apiKey,
                products: options.products,
                preview: options.preview,
                disableCaching: options.disableCaching,
            };

            if (options.endpoint) {
                state.defaultControllerOptions.endpoint = options.endpoint;
            }
        },

        /**
         * Switches a controller that is displayed in the image view.
         */
        SET_VIEW_CONTROLLER(state, controller) {
            if (controller !== state.viewController) {
                // Disconnect all event listeners from the old controller.
                if (state.viewController) {
                    state.viewController.off();
                }

                state.viewController = controller;
            }
        },

        /**
         * Saves quantity.
         */
        SET_QUANTITY(state, v) {
            state.quantity = v;
        },

        /**
         * Saves prices.
         */
        SET_PRICES(state, {
            product,
            customizations,
            subtotal,
            subtotalUnit,
            defaultPrice,
            defaultSubtotalPrice,
        }) {
            state.productPrice = product;
            state.customizationsPrice = customizations;
            state.subtotalPrice = subtotal;
            state.subtotalUnitPrice = subtotalUnit;
            state.defaultUnitPrice = defaultPrice;
            state.defaultSubtotalPrice = defaultSubtotalPrice;
        },

        SET_PERSONALIZATIONS(state, v) {
            state.personalizations = v;
        },

        SET_FREE_PERSONALIZATIONS(state, v) {
            state.freePersonalizations = v;
        },

        /**
         * Saves personalization method selection.
         */
        SET_PERSONALIZATION_METHOD(state, v) {
            state.personalizationMethod = v;
        },

        /**
         * Saves design side.
         */
        SET_DESIGN_SIDE(state, v) {
            state.designSide = v;
        },

        /**
         * Saves currently selected view.
         */
        SET_CURRENT_VIEW(state, v) {
            state.currentView = v;
        },

        /**
         * Set a placement being edited.
         */
        SET_EDIT_PLACEMENT(state, v) {
            state.editPlacements = v;
        },

        /**
         * Mark font as loaded.
         */
        SET_LOADED_FONT(state, v) {
            state.loadedFonts[v.code] = true;
        },

        /**
         * Saves bottles.
         */
        SET_BOTTLES(state, v) {
            state.secondaryBottles = v;
        },

        /**
         * Set selected bottle.
         */
        SET_SELECTED_BOTTLE(state, v) {
            state.selectedBottle = v;
        },

        SET_ADD_EDIT_MONOGRAM(state, v) {
            state.addEditMonogram = v;
        },

        SET_ADD_EDIT_TEXT(state, v) {
            state.addEditText = v;
        },

        /**
         * Sets selected bottle from scratch.
         */
        RESET_BOTTLE(state, v) {
            if (state.selectedBottle) {
                while (state.selectedBottle.length > 0) {
                    state.selectedBottle.pop();
                }

                v.forEach((l) => {
                    state.selectedBottle.push(l);
                });
            }
        },

        STASH_BOTTLES(state, placementSettings) {
            state.savedPlacements = placementSettings;

            state.stashedBottles = state.secondaryBottles.map((b) => [...b]);

            while (state.secondaryBottles.length > 1) {
                state.secondaryBottles.pop();
            }
        },

        RESTORE_BOTTLES(state) {
            if (state.stashedBottles && state.stashedBottles.length > 0) {
                while (state.secondaryBottles.length > 0) {
                    state.secondaryBottles.pop();
                }

                state.stashedBottles.forEach((b) => {
                    state.secondaryBottles.push(b);
                });

                state.stashedBottles = null;
            }
        },

        /**
         * Updates a bottle.
         */
        UPDATE_BOTTLE(state, v) {
            if (state.secondaryBottles[v.bottle] == null) {
                Vue.set(state.secondaryBottles, v.bottle, []);
            }

            Vue.set(state.secondaryBottles[v.bottle], v.line, v.text);
        },

        /**
         * Make the bottle with the longest name first.
         */
        MAKE_LONGEST_FIRST(state, index) {
            const item = state.secondaryBottles[index];

            state.secondaryBottles.splice(index, 1);

            state.secondaryBottles.unshift(item);
        },
    },

    actions: {
        /**
         * Saves options.
         */
        setCustomizerOptions({ commit }, options) {
            if (!Array.isArray(options.products)) {
                options.products = [options.products];
            }

            options.products = options.products.slice(0, 1);

            commit('SET_CUSTOMIZER_OPTIONS', options);
        },

        /**
         * Begins Loading the customizer.
         */
        load({ dispatch }) {
            dispatch('loadCustomizeFlow');
        },

        /**
         * Continue loading the main controller.
         */
        loadCustomizeFlowContinue({ state, dispatch }, { startWithProducts, recipe }) {
            let loadProducts = startWithProducts;

            if (recipe) {
                // eslint-disable-next-line
                console.log(`Switching initial loading from ${loadProducts} to ${recipe.styleCode}`);

                loadProducts = [recipe.styleCode];
            }

            const controller = new state.options.DriveCustomizer.Controller(
                { ...state.defaultControllerOptions, ...{ products: loadProducts } },
            );

            controller.on('afterInitializeState', () => {
                if (recipe) {
                    // eslint-disable-next-line
                    console.log('Restoring the state from a recipe');

                    let availabilityWarning = false;

                    recipe.components.forEach((component) => {
                        if (component.component) {
                            // Find matching component in the blueprint, otherwise isAvailable doesn't work
                            // since recipes dont include availability information.
                            const placement = controller.findPlacement(component.code);

                            if (placement && placement.components) {
                                if (placement.custom == null) {
                                    placement.custom = {};
                                }

                                if (component.custom['explicit-order']) {
                                    Object.assign(placement.custom, {
                                        'explicit-order': component.custom['explicit-order'],
                                    });
                                }

                                const match = placement.components.find((c) => c.code === component.component.code);
                                if (match && !match.placeholder) {
                                    placement.custom['v-mirror-from'] = component.custom['v-mirror-from'];
                                    placement.custom['v-mirror-to'] = component.custom['v-mirror-to'];

                                    if (controller.isAvailable(match)) {
                                        controller.updateComponent(component.code, component.component.code);

                                        Object.assign(placement.custom, {
                                            'placement-x': component.custom['placement-x'],
                                            'placement-y': component.custom['placement-y'],
                                            'placement-x-original': component.custom['placement-x-original'],
                                            'placement-y-original': component.custom['placement-y-original'],
                                        });

                                        // Copy personalization text.
                                        if (component.component.custom && component.component.custom['personalization-text']) {
                                            const copy = {};

                                            [
                                                'personalization-text',
                                                'personalization-color',
                                                'personalization-alignment',
                                                'personalization-vertical-alignment',
                                                'mask',
                                                'v-design-on',
                                                'personalization-rotate',
                                                'personalization-scale',
                                                'personalization-filled',
                                                'personalization-x',
                                                'personalization-y',
                                                'personalization-width',
                                                'personalization-height',
                                            ].forEach((k) => {
                                                copy[k] = component.component.custom[k];
                                            });

                                            placement.components.forEach((c) => {
                                                Object.assign(c.custom, copy);
                                            });
                                        }

                                        // Copy image.
                                        if (component.component.custom && component.component.custom['dynamic-image-url']) {
                                            const copy = { ...component.component.custom };

                                            [
                                                'dynamic-image-offset-x',
                                                'dynamic-image-offset-y',
                                            ].forEach((k) => {
                                                if (!Number.isNaN(copy[k])) {
                                                    copy[k] = +copy[k];
                                                }
                                            });

                                            placement.components.forEach((c) => {
                                                Object.assign(c.custom, copy);
                                            });
                                        }
                                    } else {
                                        availabilityWarning = true;
                                    }
                                }
                            }
                        }
                    });

                    if (recipe.custom['v-personalization-method']) {
                        dispatch('setPersonalizationMethod', recipe.custom['v-personalization-method']);
                    }

                    if (recipe.custom['v-groups']) {
                        try {
                            const groups = JSON.parse(recipe.custom['v-groups']);

                            if (groups.length > 0) {
                                dispatch('setIsGroupOrder', true);
                                dispatch('setSecondaryBottles', groups);
                                dispatch('setSelectedBottle', groups[0]);

                                // Enforce v1.
                                dispatch('setAppVersion', '1');
                                dispatch('closeMobileOrderTypePrompt');

                                setTimeout(() => {
                                    // Reset scroll position.
                                    document.body.scrollTop = 0;
                                    document.documentElement.scrollTop = 0;
                                });
                            }
                        } catch (e) {
                            // Nothing...
                        }
                    } else if (recipe.custom.quantity != null) {
                        dispatch('setQuantity', +recipe.custom.quantity);
                    }

                    if (availabilityWarning) {
                        dispatch('initializationWarning');
                    }
                }
            });

            controller.on('afterInitializeBlueprint', () => {
                // Load actual product options.
                dispatch('loadColors', {
                    controllers: [controller],
                });
            });

            // Load controller.
            dispatch('loadController', controller);
        },

        /**
         * Loads customization flow.
         */
        loadCustomizeFlow({ dispatch, commit, state }) {
            const uri = new Uri(window.location.href);

            const recipeId = state.options.recipe;

            if (recipeId && recipeId.length > 0 && recipeId !== 'null') {
                uri.deleteQueryParam('recipe');

                window.history.replaceState(null, '', uri.toString());

                axios.get(`//cz.drrv.co/recipe/${recipeId}.json`).then((recipeData) => {
                    const recipe = recipeData.data;

                    dispatch('loadCustomizeFlowContinue', {
                        startWithProducts: state.options.products,
                        recipe,
                    });
                });
            } else {
                dispatch('loadCustomizeFlowContinue', {
                    startWithProducts: state.options.products,
                });
            }
        },

        initializationWarning({ dispatch }) {
            // TODO: Pull i18n into actions.
            dispatch('showGenericNotify', {
                title: 'Saved products or quantities are no longer available',
                description: 'Please make new quantity or group items selections.',
                action: 'Continue',
            });
        },

        /**
         * Update translations for the current locale from the controller.
         */
        updateTranslations({ state, rootState }, controller) {
            if (controller.blueprint.translations) {
                const locale = state.options.locale || 'en';

                const translations = rootState.translationsRoot();

                let messages = translations.getLocaleMessage(locale);

                if (messages == null) {
                    messages = {};
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const k of Object.keys(controller.blueprint.translations)) {
                    messages[k] = controller.blueprint.translations[k];
                }

                translations.setLocaleMessage(locale, messages);
            }
        },

        /**
         * Select initial color as passed at the initialization time.
         */
        selectInitialColor({ state }, controller) {
            const productId = Array.isArray(state.options.products) ? state.options.products[0] : state.options.products;

            const colorPlacement = controller.findPlacement(constants.PLACEMENT_PRODUCT);

            const color = colorPlacement.components.find((x) => x.placementIndependentCode.includes(productId) && controller.isAvailable(x));

            if (color) {
                controller.updateComponent(constants.PLACEMENT_PRODUCT, color.code);
            }
        },

        /*
         * Loads product options.
         */
        loadColors({ state, dispatch, rootState }, { controllers }) {
            let showController = null;

            if (controllers.length > 0) {
                // eslint-disable-next-line
                showController = controllers[0];
            }

            if (showController) {
                dispatch('updateTranslations', showController);

                dispatch('showController', showController);

                dispatch('selectInitialColor', showController);

                const productPrinting = showController.blueprint.custom['v-product-printing'];
                const productEtching = showController.blueprint.custom['v-product-etching'];

                let method = constants.METHOD_PRINTING;

                const products = showController.products();

                if (products.some((p) => p === productPrinting)) {
                    method = constants.METHOD_PRINTING;
                }

                if (products.some((p) => p === productEtching)) {
                    method = constants.METHOD_ETCHING;
                }

                if (state.options.recipe && state.personalizationMethod) {
                    method = state.personalizationMethod;
                }

                // Find a color compatible with the method.
                const colorPlacement = showController.findPlacement(constants.PLACEMENT_PRODUCT);

                let currentColor = showController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

                let currentCompatible = false;

                if (method === constants.METHOD_PRINTING && currentColor && currentColor.custom && currentColor.custom['v-supports-printing'] === 'true') {
                    currentCompatible = true;
                }

                if (method === constants.METHOD_ETCHING && currentColor && currentColor.custom && currentColor.custom['v-supports-etching'] === 'true') {
                    currentCompatible = true;
                }

                if (!currentCompatible || !showController.isAvailable(currentColor)) {
                    let selectColor = null;

                    if (selectColor == null) {
                        colorPlacement.components.forEach((c) => {
                            if (showController.isAvailable(c)) {
                                if (selectColor == null) {
                                    if (method === constants.METHOD_PRINTING && c.custom && c.custom['v-supports-printing'] === 'true') {
                                        selectColor = c;
                                    }

                                    if (method === constants.METHOD_ETCHING && c.custom && c.custom['v-supports-etching'] === 'true') {
                                        selectColor = c;
                                    }
                                }
                            }
                        });
                    }

                    if (selectColor) {
                        showController.updateComponent(constants.PLACEMENT_PRODUCT, selectColor.code);
                    }

                    // If there are colors compatible with the preferred method, flip the method and try again.
                    if (selectColor == null) {
                        // Save the previous method in case the product needs to be reloaded.
                        if (state.personalizationMethod == null) {
                            dispatch('setPersonalizationMethod', method);
                        }

                        if (method === constants.METHOD_PRINTING) {
                            method = constants.METHOD_ETCHING;
                        } else {
                            method = constants.METHOD_PRINTING;
                        }

                        // Double check the selected color.

                        if (method === constants.METHOD_PRINTING && currentColor && currentColor.custom && currentColor.custom['v-supports-printing'] === 'true') {
                            currentCompatible = true;
                        }

                        if (method === constants.METHOD_ETCHING && currentColor && currentColor.custom && currentColor.custom['v-supports-etching'] === 'true') {
                            currentCompatible = true;
                        }

                        if (!currentCompatible) {
                            colorPlacement.components.forEach((c) => {
                                if (showController.isAvailable(c)) {
                                    if (selectColor == null) {
                                        if (method === constants.METHOD_PRINTING && c.custom && c.custom['v-supports-printing'] === 'true') {
                                            selectColor = c;
                                        }

                                        if (method === constants.METHOD_ETCHING && c.custom && c.custom['v-supports-etching'] === 'true') {
                                            selectColor = c;
                                        }
                                    }
                                }
                            });

                            if (selectColor) {
                                showController.updateComponent(constants.PLACEMENT_PRODUCT, selectColor.code);
                            }
                        } else {
                            selectColor = currentColor;
                        }

                        if (selectColor == null) {
                            // Uhm, what now?
                            dispatch('showGenericConfirm', {
                                title: rootState.translations('ALL_OUT_TITLE'),
                                description: rootState.translations('ALL_OUT_BODY'),
                                confirmAction: rootState.translations('ACTION_CONTINUE'),
                                confirmationCallback: () => {
                                    if (showController.blueprint.custom['v-stocked-handle']) {
                                        // Redirect to the product page.
                                        window.location.href = `/products/${this.viewController.blueprint.custom['v-stocked-handle']}`;
                                    } else if (showController.blueprint.custom['v-handle']) {
                                        // Redirect to the product page.
                                        window.location.href = `/products/${this.viewController.blueprint.custom['v-handle']}`;
                                    } else if (showController.blueprint.custom['v-out-of-stock-redirect']) {
                                        window.location.href = showController.blueprint.custom['v-out-of-stock-redirect'];
                                    } else {
                                        dispatch('globalCancel');
                                    }
                                },
                            });
                        }
                    }
                }

                // Retrieve the color again.
                currentColor = showController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

                // Validate quantities.
                let ats = 1;

                const productAts = currentColor ? rootState.ecommerce.products.find((p) => p.productId === currentColor.placementIndependentCode) : null;
                if (productAts) {
                    ats = productAts.ats;
                }

                let availabilityWarning = false;

                if (rootState.isGroupOrder) {
                    const bottles = state.secondaryBottles;
                    if (bottles.length > ats) {
                        availabilityWarning = true;

                        dispatch('setSecondaryBottles', bottles.slice(0, ats));
                    }
                } else if (state.quantity > ats) {
                    availabilityWarning = true;

                    dispatch('setQuantity', ats);
                }

                if (availabilityWarning) {
                    dispatch('initializationWarning');
                }

                // Figure out initial personalization method.
                dispatch('setPersonalizationMethod', method);

                if (state.options.recipe == null && state.options.selectColor != null) {
                    let selectColor = null;

                    if (state.options.selectColor) {
                        colorPlacement.components.forEach((c) => {
                            if (c.placementIndependentCode === state.options.selectColor && showController.isAvailable(c)) {
                                selectColor = c;
                            }
                        });
                    }

                    currentColor = showController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

                    if (selectColor && selectColor.code !== currentColor.code) {
                        showController.updateComponent(constants.PLACEMENT_PRODUCT, selectColor.code);

                        currentColor = showController.selectedAtPlacement(constants.PLACEMENT_PRODUCT);

                        if (method === 'printing' && currentColor.custom['v-supports-printing'] !== 'true') {
                            dispatch('setPersonalizationMethod', 'etching');
                        }

                        if (method === 'etching' && currentColor.custom['v-supports-etching'] !== 'true') {
                            dispatch('setPersonalizationMethod', 'printing');
                        }
                    }
                }
            }

            dispatch('globalBusy', false);

            dispatch('setGlobalLoaded', true);
        },

        /**
         * Displays the controller in the render view.
         */
        showController({ commit, dispatch }, controller) {
            commit('SET_VIEW_CONTROLLER', controller);

            if (controller) {
                controller.off('.store');

                controller.on('error.store', (e) => {
                    // eslint-disable-next-line
                    console.log(JSON.stringify(e));
                });

                // Listen to controller level events.
                // We wouldn't need to turn off listeners when an active controller changes since
                // previous controllers would be disabled automatically.
                controller.on('stateChanged.store', () => {
                    dispatch('updateBottles');
                    dispatch('updatePriceDebounce', controller);
                    dispatch('checkCompatiblity');
                });

                // Hide image etc components on views that they don't belong to.
                controller.on('placementHidden.store', ({ placement, view }) => {
                    const viewSurface = view.custom?.['v-design-area'];

                    if (viewSurface != null) {
                        const supports = viewSurface.split(',').map((x) => x.trim());

                        const componentSurface = placement.component.custom?.['v-design-on'];
                        if (componentSurface != null) {
                            if (!supports.includes(componentSurface)) {
                                return true;
                            }
                        }
                    }

                    return undefined;
                });

                dispatch('updatePrice', controller);

                dispatch('checkCompatiblity');
            }
        },

        /**
         * Debounced price update.
         */
        updatePriceDebounce({ dispatch }, controller) {
            if (updatePriceTimer) {
                clearTimeout(updatePriceTimer);
            }

            updatePriceTimer = setTimeout(() => {
                dispatch('updatePrice', controller);
            });
        },

        /**
         * Updates current pricing with controller information.
         */
        updatePrice({
            dispatch,
            commit,
            state,
            getters,
            rootState,
        }, controller) {
            if (controller == null) {
                return;
            }

            // Calculate the current product price.
            dispatch('getStatePrice', {
                controller,
            }).then((resolved) => {
                let subtotalUnit = 0;
                let subtotal = 0;
                let product = 0;
                let customizations = 0;
                let defaultPrice = 0;
                let defaultSubtotalPrice = 0;

                resolved.breakdown.forEach((placement) => {
                    subtotal += placement.current;

                    if (placement.placement === constants.PLACEMENT_PRODUCT) {
                        product = placement.current;
                    } else {
                        customizations += placement.current;
                    }
                });

                let { quantity } = state;

                if (rootState.isGroupOrder) {
                    quantity = state.secondaryBottles.length;
                }

                const { priceTiers, defaultUnitPrice } = getters;

                if (priceTiers) {
                    const tiers = [...priceTiers];
                    tiers.reverse();

                    const selectedTier = tiers.find((p) => p.quantity <= quantity);

                    if (selectedTier) {
                        if (selectedTier.amount.includes('%')) {
                            const discount = parseFloat(selectedTier.amount);

                            product = (product * (100 - discount)) / 100;
                            subtotal = (subtotal * (100 - discount)) / 100;
                        } else {
                            product -= parseFloat(selectedTier.amount);
                            subtotal -= parseFloat(selectedTier.amount);
                        }
                    }
                }

                defaultSubtotalPrice = defaultUnitPrice * quantity;

                if (rootState.ecommerce.personalizationProduct) {
                    const areas = {};

                    // Calculate the price for each used personalization area.
                    controller.state.forEach((s) => {
                        if (s.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)
                            || s.placement.code.startsWith(constants.PLACEMENT_IMAGE)
                            || s.placement.code.startsWith(constants.PLACEMENT_TEXT)) {
                            const on = s.component.custom['v-design-on'];

                            if (on && isFilled(s.component)) {
                                areas[on] = true;
                            }
                        }
                    });

                    const areasCount = Object.keys(areas).length;

                    let personalizationPrice = 0;
                    let freePersonalizations = 0;

                    for (let i = 0; i < areasCount; i += 1) {
                        let personalizationProduct = rootState.ecommerce.personalizationProductByPosition[i];

                        if (personalizationProduct) {
                            freePersonalizations += 1;
                        }

                        if (personalizationProduct == null) {
                            personalizationProduct = rootState.ecommerce.personalizationProduct;
                        }

                        if (personalizationProduct) {
                            personalizationPrice += personalizationProduct.price;
                        }
                    }

                    // Unit price.
                    subtotal += personalizationPrice;

                    // Total price.
                    defaultSubtotalPrice += personalizationPrice * quantity;

                    // Unit price.
                    customizations += personalizationPrice;

                    commit('SET_PERSONALIZATIONS', areasCount);
                    commit('SET_FREE_PERSONALIZATIONS', freePersonalizations);
                } else {
                    commit('SET_PERSONALIZATIONS', 0);
                    commit('SET_FREE_PERSONALIZATIONS', 0);
                }

                if (defaultUnitPrice.toFixed) {
                    Promise.all([
                        dispatch('formatPrice', (subtotal).toFixed(2)).then((v) => {
                            subtotalUnit = v;
                        }),
                        dispatch('formatPrice', (subtotal * quantity).toFixed(2)).then((v) => {
                            subtotal = v;
                        }),
                        dispatch('formatPrice', (product * quantity).toFixed(2)).then((v) => {
                            product = v;
                        }),
                        dispatch('formatPrice', (customizations).toFixed(2)).then((v) => {
                            customizations = v;
                        }),
                        dispatch('formatPrice', (defaultUnitPrice).toFixed(2)).then((v) => {
                            defaultPrice = v;
                        }),
                        dispatch('formatPrice', (defaultSubtotalPrice).toFixed(2)).then((v) => {
                            defaultSubtotalPrice = v;
                        }),
                    ]).then(() => {
                        commit('SET_PRICES', {
                            subtotalUnit,
                            subtotal,
                            product,
                            customizations,
                            defaultPrice,
                            defaultSubtotalPrice,
                        });
                    });
                }
            });
        },

        /**
         * Continue saving a recipe.
         */
        tosConfirmed({ dispatch }) {
            dispatch('customizerDone');

            Cookies.set('CUSTOM-TOS', 'accepted');
        },

        /**
         * Save recipe
         */
        customizerDone({
            dispatch,
            state,
            rootState,
        }, options) {
            return new Promise((resolve) => {
                const recipeProcessor = {
                    accepted(index) {
                        return index < state.secondaryBottles.length;
                    },

                    translateCode(componentCode) {
                        return componentCode;
                    },

                    translateRecipe(recipe) {
                        if (!rootState.isGroupOrder) {
                            recipe.custom.quantity = state.quantity.toString();
                        } else {
                            recipe.custom.quantity = '1';
                        }

                        recipe.custom['v-personalization-method'] = state.personalizationMethod;

                        const groups = [];

                        if (state.secondaryBottles && state.secondaryBottles.length > 0) {
                            state.secondaryBottles.forEach((item) => {
                                groups.push(item);
                            });
                        }

                        if (rootState.isGroupOrder && state.secondaryBottles.length > 0 && recipe.custom['batch-index']) {
                            recipe.custom['v-groups'] = JSON.stringify(groups);

                            const batchIndex = +recipe.custom['batch-index'];

                            const bottle = state.secondaryBottles[batchIndex];

                            const mirroredTextPlacements = [];

                            const filledTextPlacements = recipe.components.filter((c) => {
                                if (c.custom && c.custom['v-mirror-from']) {
                                    mirroredTextPlacements.push(c);

                                    c.component.custom['personalization-text'] = '';

                                    return false;
                                }

                                if (c.component.custom && c.component.custom['personalization-filled'] === 'true') {
                                    return true;
                                }

                                return false;
                            });

                            filledTextPlacements.forEach((p, index) => {
                                p.component.custom['personalization-text'] = (bottle[index] || '');

                                if (p.custom && p.custom['v-mirror-to']) {
                                    const to = mirroredTextPlacements.find((x) => x.code === p.custom['v-mirror-to']);

                                    if (to) {
                                        to.component.custom['personalization-text'] = p.component.custom['personalization-text'];
                                    }
                                }
                            });

                            if (batchIndex === 0) {
                                filledTextPlacements.forEach((p) => {
                                    p.component.custom['personalization-size-fit-min'] = 'none';

                                    if (p.custom && p.custom['v-mirror-to']) {
                                        const to = mirroredTextPlacements.find((x) => x.code === p.custom['v-mirror-to']);

                                        if (to) {
                                            to.component.custom['personalization-size-fit-min'] = p.component.custom['personalization-size-fit-min'];
                                        }
                                    }
                                });
                            } else {
                                filledTextPlacements.forEach((p) => {
                                    p.component.custom['personalization-size-fit-min'] = '1';

                                    if (p.custom && p.custom['v-mirror-to']) {
                                        const to = mirroredTextPlacements.find((x) => x.code === p.custom['v-mirror-to']);

                                        if (to) {
                                            to.component.custom['personalization-size-fit-min'] = p.component.custom['personalization-size-fit-min'];
                                        }
                                    }
                                });
                            }
                        }
                    },
                };

                function downloadRecipes(recipes) {
                    return new Promise((after) => {
                        const tasks = [];

                        recipes.forEach((recipe) => {
                            tasks.push(new Promise((resolveRecipe) => {
                                axios.get(recipe.location).then((recipeResponse) => {
                                    const { data } = recipeResponse;

                                    const selected = state.viewController.selectedAtPlacement('Product');

                                    dispatch('getVariant', selected.custom['v-sku']).then((variant) => {
                                        recipe.variant = variant;
                                        recipe.addons = [];
                                        recipe.quantity = +(data.custom.quantity || '1');

                                        // Calculate personalization products.
                                        if (rootState.ecommerce.personalizationProduct) {
                                            const areas = {};

                                            // Calculate the price for each used personalization area.
                                            data.components.forEach((s) => {
                                                if (s.code.startsWith(constants.PLACEMENT_MONOGRAM)
                                                    || s.code.startsWith(constants.PLACEMENT_IMAGE)
                                                    || s.code.startsWith(constants.PLACEMENT_TEXT)) {
                                                    const on = s.component.custom['v-design-on'];

                                                    if (on && isFilled(s.component)) {
                                                        areas[on] = true;
                                                    }
                                                }
                                            });

                                            let areasCount = Object.keys(areas).length;

                                            if (areasCount > 0 && rootState.ecommerce.personalizationProductByPosition[0]) {
                                                // Use it on the first addon.
                                                recipe.addons.push({
                                                    variant: rootState.ecommerce.personalizationProductByPosition[0].variant,
                                                    quantity: 1 * recipe.quantity,
                                                });

                                                areasCount -= 1;
                                            }

                                            if (areasCount > 0) {
                                                recipe.addons.push({
                                                    variant: rootState.ecommerce.personalizationProduct.variant,
                                                    quantity: areasCount * recipe.quantity,
                                                });
                                            }
                                        }

                                        recipe.views = data.views.map((v) => ({
                                            code: v.code,
                                            name: v.name,
                                            previewPng: v.previewPng,
                                            previewJpg: v.previewJpg,
                                            production: v.production,
                                        }));

                                        resolveRecipe();
                                    });
                                });
                            }));
                        });

                        Promise.all(tasks).then(() => {
                            after();
                        });
                    });
                }

                function continueSaving() {
                    if (rootState.isGroupOrder) {
                        state.viewController.saveBatch(recipeProcessor).then((recipes) => {
                            dispatch('globalBusy', false);

                            downloadRecipes(recipes).then(() => {
                                if (options && options.cancelAfter) {
                                    dispatch('globalSaveCancel', {
                                        type: 'custom',
                                        recipe: recipes[0],
                                        group: recipes,
                                    });

                                    resolve();
                                } else if (options && options.saveOnly) {
                                    resolve({
                                        type: 'custom',
                                        recipe: recipes[0],
                                        group: recipes,
                                    });
                                } else {
                                    dispatch('globalDone', {
                                        type: 'custom',
                                        recipe: recipes[0],
                                        group: recipes,
                                    });

                                    resolve();
                                }
                            });
                        });
                    } else {
                        state.viewController.saveRecipe(recipeProcessor).then((recipe) => {
                            dispatch('globalBusy', false);

                            downloadRecipes([recipe]).then(() => {
                                if (options && options.cancelAfter) {
                                    dispatch('globalSaveCancel', {
                                        type: 'custom',
                                        recipe,
                                    });

                                    resolve();
                                } else if (options && options.saveOnly) {
                                    resolve({
                                        type: 'custom',
                                        recipe,
                                    });
                                } else {
                                    dispatch('globalDone', {
                                        type: 'custom',
                                        recipe,
                                    });

                                    resolve();
                                }
                            });
                        });
                    }
                }

                if (state.viewController) {
                    if (!options || !options.noBusy) {
                        dispatch('globalBusy', true);
                    }

                    continueSaving();
                } else {
                    dispatch('globalCancel');

                    resolve();
                }
            });
        },

        /**
         * Set order quantity.
         */
        setQuantity({ commit, state, dispatch }, v) {
            commit('SET_QUANTITY', v);

            if (state.viewController) {
                dispatch('updatePriceDebounce', state.viewController);
            }
        },

        /**
         * Clears placement.
         */
        clearPlacement({ state }, options) {
            const controller = options.controller || state.viewController;
            const placement = options.controller ? options.placement : options;

            const placementCode = placement.placement.code;
            const selectedComponent = controller.selectedAtPlacement(placementCode);
            const selectedPlacement = controller.findPlacement(placementCode);

            if (selectedComponent.custom['dynamic-image-url'] && selectedComponent.custom['dynamic-image-url'].length > 0) {
                controller.updateDynamicImage(placementCode, {
                    title: '',
                    name: '',
                    url: '',
                    pngUrl: '',
                    jpgUrl: '',
                    width: '',
                    height: '',
                });
            }

            if (selectedComponent.custom['personalization-text'] && selectedComponent.custom['personalization-text'].length > 0) {
                controller.updatePersonalization(placementCode, '');
            }

            controller.commitCustomPlacementAttributes(placementCode, {
                'v-mirror-from': null,
                'v-mirror-to': null,
            });

            controller.commitCustomAttributes(placementCode, {
                'personalization-filled': null,
                'personalization-size-fit-min': 'none',
            }, {
                propagate: true,
            });

            // Clear rendering specific things.
            selectedPlacement.custom['last-personalization-x'] = null;
            selectedPlacement.custom['last-personalization-y'] = null;

            controller.clearComponent(placementCode);
        },

        /**
         * Resets Customizations and revert selected step
         */
        resetCustomizations({ state, dispatch }) {
            if (!state.viewController) {
                return;
            }

            const placements = state.viewController.state;

            placements.forEach((placement) => {
                const { code } = placement.placement;
                if (code !== 'Product') {
                    dispatch('clearPlacement', placement);
                }
            });

            dispatch('selectStep', null);
        },

        /**
         * Sets personalization method.
         */
        setPersonalizationMethod({ state, commit, dispatch }, v) {
            return new Promise((resolve) => {
                if (state.personalizationMethod != null && state.personalizationMethod !== v) {
                    let product;

                    // Reload controller with a product appropriate for the method.
                    if (v === constants.METHOD_PRINTING) {
                        product = state.viewController.blueprint.custom['v-product-printing'];
                    }

                    if (v === constants.METHOD_ETCHING) {
                        product = state.viewController.blueprint.custom['v-product-etching'];
                    }

                    if (product) {
                        // eslint-disable-next-line
                        console.log(`Reloading with ${product}`);

                        const reloaded = new state.options.DriveCustomizer.Controller({
                            ...state.defaultControllerOptions,
                            ...{
                                products: [product],
                            },
                        });

                        reloaded.on('afterInitializeBlueprint', () => {
                            reloaded.transferCompatibleState(state.viewController);

                            const designable = (reloaded.blueprint.custom['v-design-on'] || '').split(',').map((x) => x.trim());

                            // Check for compatible images.
                            reloaded.state.forEach((s) => {
                                let compatible = true;

                                const designOn = s.component.custom['v-design-on'];

                                let areaWidth = '1';
                                let areaHeight = '1';

                                if (designOn) {
                                    if (!designable.includes(designOn)) {
                                        compatible = false;
                                    }

                                    const area = designArea(state, designOn);

                                    areaWidth = area.width;
                                    areaHeight = area.height;
                                }

                                if (s.placement.code.startsWith(constants.PLACEMENT_IMAGE)) {
                                    const flags = s.component.custom['gallery-flags'] || 'EP';

                                    if (state.personalizationMethod === 'etching' && !flags.includes('E')) {
                                        compatible = false;
                                    }

                                    if (state.personalizationMethod === 'printing' && !flags.includes('P')) {
                                        compatible = false;
                                    }
                                }

                                if (!compatible) {
                                    dispatch('clearPlacement', {
                                        controller: reloaded,
                                        placement: s,
                                    });

                                    const updates = {};

                                    if (s.placement.code.startsWith(constants.PLACEMENT_IMAGE)) {
                                        updates['dynamic-image-area-width'] = areaWidth.toString();
                                        updates['dynamic-image-area-height'] = areaHeight.toString();
                                    }

                                    reloaded.commitCustomAttributes(s.placement.code, updates, {
                                        propagate: true,
                                    });
                                } else {
                                    // Reassign masks, etc.
                                    const view = reloaded.blueprint.views.find((x) => x.custom['v-design-area'] === designOn);

                                    if (view) {
                                        const updates = {
                                            mask: view.custom['v-design-mask'],
                                        };

                                        if (s.placement.code.startsWith(constants.PLACEMENT_IMAGE)) {
                                            updates['dynamic-image-area-width'] = areaWidth.toString();
                                            updates['dynamic-image-area-height'] = areaHeight.toString();
                                        }

                                        if (isFilled(s.component)) {
                                            const {
                                                left: oldLeft, top: oldTop, right: oldRight, bottom: oldBottom,
                                            } = designArea(state, designOn);

                                            const {
                                                left, top, right, bottom,
                                            } = designArea(state, designOn, reloaded);

                                            const bbox = extent(s.placement, s.component);

                                            if (oldLeft != null && left != null) {
                                                if (s.component.custom['widget-type'] === 'personalization') {
                                                    const xRatio = (bbox.x - oldLeft) / (oldRight - oldLeft);
                                                    const yRatio = (bbox.y - oldTop) / (oldBottom - oldTop);

                                                    const x = left + (right - left) * xRatio - bbox.w;
                                                    const y = top + (bottom - top) * yRatio - bbox.h;

                                                    updates['personalization-x'] = x.toString();
                                                    updates['personalization-y'] = y.toString();
                                                }

                                                if (s.component.custom['widget-type'] === 'dynamic-image') {
                                                    const xRatio = (bbox.x - oldLeft) / (oldRight - oldLeft);
                                                    const yRatio = (bbox.y - oldTop) / (oldBottom - oldTop);

                                                    const x = left + (right - left) * xRatio;
                                                    const y = top + (bottom - top) * yRatio;

                                                    updates['dynamic-image-offset-x'] = x.toString();
                                                    updates['dynamic-image-offset-y'] = y.toString();
                                                }
                                            }
                                        }

                                        reloaded.commitCustomAttributes(s.placement.code, updates, {
                                            propagate: true,
                                        });
                                    }
                                }
                            });

                            setTimeout(() => {
                                dispatch('showController', reloaded);

                                setTimeout(() => {
                                    resolve();
                                });
                            });
                        });

                        dispatch('loadController', reloaded);

                        commit('SET_PERSONALIZATION_METHOD', v);
                    }
                } else {
                    commit('SET_PERSONALIZATION_METHOD', v);

                    resolve();
                }
            });
        },

        /**
         * Sets design side.
         */
        setDesignSide({ commit }, v) {
            commit('SET_DESIGN_SIDE', v);
        },

        /**
         * Saves current view reference.
         */
        setCurrentView({ commit }, v) {
            commit('SET_CURRENT_VIEW', v);
        },

        /**
         * Set placement to edit.
         */
        editPlacement({ commit }, v) {
            if (v == null || Array.isArray(v)) {
                commit('SET_EDIT_PLACEMENT', v);
            } else {
                commit('SET_EDIT_PLACEMENT', [v]);
            }
        },

        /**
         * Loads web font files.
         */
        loadFonts({ commit, state }, fonts) {
            fonts.forEach((font) => {
                if (!state.loadedFonts[font.code]) {
                    if (font.custom['v-webfont']) {
                        const el = document.createElement('link');

                        el.setAttribute('rel', 'stylesheet');
                        el.setAttribute('type', 'text/css');
                        el.setAttribute('href', font.custom['v-webfont']);

                        document.querySelector('head').appendChild(el);

                        commit('SET_LOADED_FONT', font);
                    }
                }
            });
        },

        /**
         * Saves new bottles.
         */
        setSecondaryBottles({ commit, dispatch, state }, bottles) {
            commit('SET_BOTTLES', bottles);

            dispatch('updatePriceDebounce', state.viewController);
        },

        /**
         * Selects bottle to display.
         */
        setSelectedBottle({ state, getters, commit }, bottle) {
            commit('SET_SELECTED_BOTTLE', bottle);

            const bottleIndex = state.secondaryBottles.indexOf(bottle);

            getters.filledTextPlacements.forEach((p, index) => {
                const text = bottle[index] || '';

                state.viewController.updatePersonalization(p.placement.code, text);

                if (bottleIndex === 0) {
                    // Remove autosizing.
                    state.viewController.commitCustomAttributes(p.placement.code, {
                        'personalization-size-fit-min': 'none',
                    });
                } else {
                    // Enable autosizing.
                    state.viewController.commitCustomAttributes(p.placement.code, {
                        'personalization-size-fit-min': '1',
                    });
                }
            });
        },

        /**
         * Enforce the rule that bottles with the longest name should be first. Update the personalization sizing as well.
         */
        enforceBottlesOrder({
            state,
            dispatch,
            commit,
            rootState,
        }) {
            if (state.secondaryBottles != null && state.secondaryBottles.length > 0) {
                // Find the bottle with the longest text.
                let longestText = null;
                let longestIndex = null;

                state.secondaryBottles.forEach((item, index) => {
                    if (item && item.length > 0) {
                        let text = null;

                        item.forEach((v) => {
                            if (text == null || text.length < v.length) {
                                text = v;
                            }
                        });

                        if (text != null) {
                            if (longestText == null || longestText.length < text.length) {
                                longestText = text;
                                longestIndex = index;
                            }
                        }
                    }
                });

                if (longestIndex > 0) {
                    // OK, now we need to reshuffle items.

                    // First, swap the bottles.
                    commit('MAKE_LONGEST_FIRST', longestIndex);

                    // Make the new one selected.
                    dispatch('setSelectedBottle', state.secondaryBottles[0]);

                    // Show the warning.
                    dispatch('showGenericNotify', {
                        title: rootState.translations('LONGEST_NAME_WARNING_TITLE'),
                        description: rootState.translations('LONGEST_NAME_WARNING'),
                        action: rootState.translations('ACTION_CONTINUE'),
                    });
                }
            }
        },

        /**
         * Update secondary bottle personalization.
         */
        updateSecondaryBottle({ commit }, changes) {
            commit('UPDATE_BOTTLE', changes);
        },

        /**
         * Updates current bottle from state.
         */
        updateBottles({ state, commit, getters }) {
            if (state.secondaryBottles.length === 0) {
                commit('SET_BOTTLES', [[]]);
            }

            if (state.selectedBottle == null) {
                commit('SET_SELECTED_BOTTLE', state.secondaryBottles[0]);
            }

            const lines = [];

            getters.filledTextPlacements.forEach((p) => {
                const text = p.component.custom['personalization-text'];

                lines.push(text);
            });

            commit('RESET_BOTTLE', lines);
        },

        /**
         * Switch rendering view to the currently edited view surface.
         */
        switchToDesignView({ state, dispatch }, designOn) {
            if (state.viewController) {
                const designSurface = state.viewController.blueprint.views.find((v) => {
                    if (v.custom && v.custom['v-design-area'] === designOn) {
                        return true;
                    }

                    return false;
                });

                if (designSurface) {
                    dispatch('globalEvent', {
                        event: 'switchToView',
                        data: {
                            view: designSurface,
                        },
                    });
                }
            }
        },

        /**
         * Configures a text placement.
         */
        takeEmptyPlacement({ state }, placement) {
            let topmost = null;

            state.viewController.state.forEach((s) => {
                if (!s.placement.code.includes('Embellishments')) {
                    const index = +s.placement.custom['explicit-order'];

                    if (topmost == null || index > topmost) {
                        topmost = index;
                    }
                }
            });

            if (topmost != null) {
                placement.custom['explicit-order'] = topmost + 1;
            }
        },

        /**
         * Shifts the layer up in the stacking order.
         */
        layerUp({ state }, placement) {
            const index = +placement.custom['explicit-order'];

            let above = null;
            let abovePlacement = null;

            // Find a layer that is directly above this one.
            state.viewController.state.forEach((s) => {
                if (
                    isFilled(s.component)
                    && s.placement.code !== placement.code
                    && s.placement.code !== constants.PLACEMENT_PRODUCT
                    && s.placement.code !== constants.PLACEMENT_EMBELLISHMENTS
                    && s.placement.code !== constants.PLACEMENT_EMBELLISHMENTS_BASE) {
                    const anotherIndex = +s.placement.custom['explicit-order'];

                    if (anotherIndex > index && (above == null || anotherIndex < above)) {
                        above = anotherIndex;
                        abovePlacement = s.placement;
                    }
                }
            });

            if (above != null) {
                state.viewController.commitCustomPlacementAttributes(placement.code, {
                    'explicit-order': above,
                });

                state.viewController.commitCustomPlacementAttributes(abovePlacement.code, {
                    'explicit-order': index,
                });
            }
        },

        /**
         * Shifts the layer down in the stacking order.
         */
        layerDown({ state }, placement) {
            const index = +placement.custom['explicit-order'];

            let below = null;
            let belowPlacement = null;

            // Find a layer that is directly below this one.
            state.viewController.state.forEach((s) => {
                if (
                    isFilled(s.component)
                    && s.placement.code !== placement.code
                    && s.placement.code !== constants.PLACEMENT_PRODUCT
                    && s.placement.code !== constants.PLACEMENT_EMBELLISHMENTS
                    && s.placement.code !== constants.PLACEMENT_EMBELLISHMENTS_BASE) {
                    const anotherIndex = +s.placement.custom['explicit-order'];

                    if (anotherIndex < index && (below == null || anotherIndex > below)) {
                        below = anotherIndex;
                        belowPlacement = s.placement;
                    }
                }
            });

            if (below != null) {
                state.viewController.commitCustomPlacementAttributes(placement.code, {
                    'explicit-order': below,
                });

                state.viewController.commitCustomPlacementAttributes(belowPlacement.code, {
                    'explicit-order': index,
                });
            }
        },

        /**
         * Moves the design to another surface if it moved too far out the design zone.
         */
        layoutMoved() {
        },

        stashBottles({
            state,
            commit,
            dispatch,
            getters,
        }) {
            const settings = [];

            getters.filledTextPlacements.forEach((textPlacement) => {
                settings.push({
                    'v-design-on': textPlacement.component.custom['v-design-on'],
                    group: textPlacement.component.custom['v-group'],
                    'group-width': textPlacement.component.custom['v-group-width'],
                    'group-height': textPlacement.component.custom['v-group-height'],
                    mask: textPlacement.component.custom.mask,
                    'personalization-x': textPlacement.component.custom['personalization-x'],
                    'personalization-y': textPlacement.component.custom['personalization-y'],
                    'personalization-rotate': textPlacement.component.custom['personalization-rotate'],
                    'personalization-scale': textPlacement.component.custom['personalization-scale'],
                    'personalization-filled': textPlacement.component.custom['personalization-filled'],
                    'personalization-color': textPlacement.component.custom['personalization-color'],
                });
            });

            commit('STASH_BOTTLES', settings);

            dispatch('setSelectedBottle', state.secondaryBottles[0]);
        },

        restoreBottles({
            state,
            commit,
            dispatch,
            getters,
        }) {
            // Remove monogramming.
            [...getters.monogramPlacements].forEach((textPlacement) => {
                dispatch('clearPlacement', textPlacement);
            });

            // Remove text placements.
            [...getters.textPlacements].forEach((textPlacement) => {
                dispatch('clearPlacement', textPlacement);
            });

            if (state.savedPlacements) {
                state.savedPlacements.forEach((savedPlacement) => {
                    if (getters.emptyTextPlacements.length > 0) {
                        const freePlacement = getters.emptyTextPlacements[0];

                        state.viewController.commitCustomAttributes(freePlacement.placement.code, {
                            'v-design-on': savedPlacement['v-design-on'],
                            mask: savedPlacement.mask,
                            'v-group': savedPlacement.group,
                            'v-group-width': savedPlacement['group-width'],
                            'v-group-height': savedPlacement['group-height'],
                            'personalization-x': savedPlacement['personalization-x'],
                            'personalization-y': savedPlacement['personalization-y'],
                            'personalization-rotate': savedPlacement['personalization-rotate'],
                            'personalization-scale': savedPlacement['personalization-scale'],
                            'personalization-filled': savedPlacement['personalization-filled'],
                            'personalization-color': savedPlacement['personalization-color'],
                        });

                        dispatch('setGroup', {
                            placement: freePlacement.placement.code,
                            group: savedPlacement.group,
                        });
                    }
                });
            }

            commit('RESTORE_BOTTLES');

            dispatch('setSelectedBottle', state.secondaryBottles[0]);
        },

        saveWishlist({ state, dispatch }, options) {
            if (options && options.id) {
                if (state.options && state.options.onSave) {
                    state.options.onSave(options.id);
                }

                if (options && options.cancelAfter) {
                    dispatch('globalCancel');
                }
            } else {
                dispatch('customizerDone', {
                    saveOnly: true,
                }).then((recipe) => {
                    if (state.options && state.options.onSave) {
                        state.options.onSave(recipe.recipe.id);
                    }

                    if (options && options.cancelAfter) {
                        dispatch('globalCancel');
                    }
                });
            }
        },

        enforceMirroring({ state, dispatch }, options) {
            const update = () => {
                const useController = (options && options.controller) ? options.controller : state.viewController;

                if (useController) {
                    let updated = false;

                    useController.state.forEach((p) => {
                        if (p.placement && p.placement.custom && p.placement.custom['v-mirror-from']) {
                            const from = p.placement.custom['v-mirror-from'];

                            const source = useController.state.find((x) => x.placement.code === from);

                            if (source && isFilled(source.component)) {
                                p.placement.custom['explicit-order'] = source.placement.custom['explicit-order'];

                                const { component } = source;

                                // Find a similar component for the mirrored placement.

                                let mirrorComponent = null;
                                p.placement.components.forEach((c) => {
                                    if (mirrorComponent) {
                                        return;
                                    }

                                    let allMatch = true;

                                    component.description.forEach((d) => {
                                        if (!c.description.some((d2) => d2.code === d.code)) {
                                            allMatch = false;
                                        }
                                    });

                                    if (allMatch) {
                                        mirrorComponent = c;
                                    }
                                });

                                if (mirrorComponent) {
                                    if (p.component.code !== mirrorComponent.code) {
                                        useController.updateComponent(p.placement.code, mirrorComponent.code);
                                        updated = true;
                                    }
                                }

                                const designOn = source.component.custom['v-design-on'];
                                let mirrorOn = designOn;
                                if (designOn === 'back') {
                                    mirrorOn = 'front';
                                } else {
                                    mirrorOn = 'back';
                                }

                                const {
                                    left,
                                } = designArea(state, mirrorOn);

                                const {
                                    left: oldLeft,
                                } = designArea(state, designOn);

                                const mirrorredView = useController.blueprint.views.find((v) => v.custom['v-design-area'] === mirrorOn);
                                const mask = mirrorredView.custom['v-design-mask'];

                                if (source.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)
                                    || source.placement.code.startsWith(constants.PLACEMENT_TEXT)) {
                                    const copy = {};

                                    [
                                        'personalization-text',
                                        'personalization-color',
                                        'personalization-alignment',
                                        'personalization-vertical-alignment',
                                        'mask',
                                        'v-design-on',
                                        'personalization-rotate',
                                        'personalization-scale',
                                        'personalization-filled',
                                        'personalization-x',
                                        'personalization-y',
                                        'personalization-width',
                                        'personalization-height',
                                    ].forEach((k) => {
                                        copy[k] = source.component.custom[k];
                                    });

                                    let x = +copy['personalization-x'];
                                    x = x - oldLeft + left;
                                    copy['personalization-x'] = x;

                                    copy['v-design-on'] = mirrorOn;
                                    copy.mask = mask;

                                    const values = Object.entries(copy);

                                    p.placement.components.forEach((c) => {
                                        for (let i = 0; i < values.length; i += 1) {
                                            if (c.custom[values[i][0]] !== values[i][1]) {
                                                Vue.set(c.custom, values[i][0], values[i][1]);

                                                updated = true;
                                            }
                                        }
                                    });
                                }

                                if (source.placement.code.startsWith(constants.PLACEMENT_IMAGE)) {
                                    const copy = { ...source.component.custom };

                                    [
                                        'dynamic-image-offset-x',
                                        'dynamic-image-offset-y',
                                    ].forEach((k) => {
                                        if (!Number.isNaN(copy[k])) {
                                            copy[k] = +copy[k];
                                        }
                                    });

                                    let x = +copy['dynamic-image-offset-x'];
                                    x = x - oldLeft + left;
                                    copy['dynamic-image-offset-x'] = x;

                                    copy['v-design-on'] = mirrorOn;
                                    copy.mask = mask;

                                    const values = Object.entries(copy);

                                    p.placement.components.forEach((c) => {
                                        for (let i = 0; i < values.length; i += 1) {
                                            if (c.custom[values[i][0]] !== values[i][1]) {
                                                Vue.set(c.custom, values[i][0], values[i][1]);

                                                updated = true;
                                            }
                                        }
                                    });
                                }
                            } else {
                                // eslint-disable-next-line
                                console.log(`Disabling mirroring to ${p.placement.code}`);

                                delete p.placement.custom['v-mirror-from'];

                                dispatch('clearPlacement', p);
                            }
                        }
                    });

                    if (updated) {
                        useController.emit('stateChanged');
                    }
                }
            };

            if (updateMirroringTimer) {
                clearTimeout(updateMirroringTimer);
            }

            if (options && options.immediate) {
                update(options);
            } else {
                updateMirroringTimer = setTimeout(update);
            }
        },

        /**
         * Checks compatiblity between the selected bottle color and image or text colors.
         */
        checkCompatiblity({ state, getters }) {
            const colors = getters.availablePersonalizationColors;

            state.viewController.state.forEach((s) => {
                let color = null;

                color = s.component.custom['personalization-color'];

                if (color && color.length > 0) {
                    const available = colors.find((c) => c.custom.hex.toLowerCase() === color.toLowerCase());
                    if (!available) {
                        // Pick some other color.
                        const another = colors.find((c) => c.custom.hex === '000000') ?? colors[0];

                        state.viewController.commitCustomAttributes(s.placement.code, {
                            'personalization-color': another.custom.hex,
                        });
                    }
                }

                color = s.component.custom['effect-colorize-add'];

                if (color && color.length > 0) {
                    const available = colors.find((c) => c.custom.hex.toLowerCase() === color.toLowerCase());
                    if (!available) {
                        // Pick some other color.
                        const another = colors.find((c) => c.custom.hex === '000000') ?? colors[0];

                        state.viewController.commitCustomAttributes(s.placement.code, {
                            'effect-colorize-add': another.custom.hex,
                            'effect-colorize-name': another.name,
                        });
                    }
                }
            });
        },

        /**
         * Renders embellishments only on the selected view.
         */
        renderOnlyOnView() {
        },

        /**
         * Switches to a common view if there is already a component configured.
         */
        switchToCommonView() {
        },

        /**
         * Move all components over if necessary.
         */
        moveEverythingOver() {
        },

        /**
         * Assign a rendering group to a placement.
         */
        setGroup({ state }, { placement, group }) {
            state.viewController.blueprint.views.forEach((view) => {
                // Skip production views.
                if (view.productionReady) {
                    return;
                }

                const p = view.placements.find((x) => x.code === placement);
                if (p) {
                    if (p.custom == null) {
                        p.custom = {};
                    }

                    // Find the base layer in this view.
                    const baseLayer = view.placements.find((x) => x.code.startsWith('EmbellishmentsBase') && x.custom?.group === group);

                    // Base layer is required...
                    if (!baseLayer) {
                        // Assign some random group that wouldn't be rendered anyway.
                        p.custom.hidden = 'true';
                        return;
                    }

                    p.custom.hidden = 'false';

                    p.custom.group = group;

                    // Copy dimensions too, since the LayerEditor wants them...
                    p.custom['group-width'] = baseLayer.custom['group-width'];
                    p.custom['group-height'] = baseLayer.custom['group-height'];
                }
            });
        },
    },
};
